import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Book, ChevronRight } from 'lucide-react';

// Background animé
const AnimatedBackground = () => (
  <div className="fixed inset-0 -z-10">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(1,125,126,0.15),transparent_50%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center_left,rgba(0,169,157,0.1),transparent_50%)]" />
    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-500/50 to-transparent" />
    <div className="absolute inset-0 opacity-30">
      <div className="absolute inset-0"
        style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '4rem 4rem',
          maskImage: 'radial-gradient(circle at 50% 50%, black, transparent 80%)'
        }}
      />
    </div>
  </div>
);

// Breadcrumb component
const Breadcrumb = ({ productName }) => {
  const navigate = useNavigate();
  
  return (
    <div className="flex items-center gap-2 text-sm text-white/50 mb-8">
      <button
        onClick={() => navigate(-1)}
        className="hover:text-white transition-colors flex items-center gap-1"
      >
        <ArrowLeft className="w-4 h-4" />
        Retour
      </button>
      <ChevronRight className="w-4 h-4" />
      <span className="text-white/70">Documentation</span>
      <ChevronRight className="w-4 h-4" />
      <span className="text-white">{productName}</span>
    </div>
  );
};

const DocsPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [documentation, setDocumentation] = useState(null);

  useEffect(() => {
    const loadDocumentation = async () => {
      try {
        // Import dynamique de la documentation
        const module = await import(`../solutions/docs/${productId}.js`);
        setDocumentation(module.default);
      } catch (error) {
        console.error('Error loading documentation:', error);
        setDocumentation(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadDocumentation();
  }, [productId]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mb-4" />
          <p className="text-white/70">Chargement de la documentation...</p>
        </div>
      </div>
    );
  }

  if (!documentation) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Documentation non trouvée</h2>
          <p className="text-white/70 mb-6">
            La documentation pour cette solution n'est pas disponible.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 rounded-lg bg-[#017D7E] text-white font-medium
                     hover:bg-[#015F60] transition-all"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white pt-20 md:pt-32">
      <AnimatedBackground />
      
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb productName={documentation.title} />
          
          <div className="prose prose-invert max-w-none">
            <div className="bg-white/5 rounded-xl p-6 md:p-8 border border-white/10">
              <div className="flex items-center gap-4 mb-8">
                <div className="p-3 rounded-xl bg-[#017D7E]/10">
                  <Book className="w-6 h-6 text-[#017D7E]" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold mb-2">{documentation.title}</h1>
                  <p className="text-white/70">{documentation.description}</p>
                </div>
              </div>

              {/* Contenu de la documentation */}
              <div className="space-y-8">
                {documentation.content}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocsPage;